/**
 * useOmnipresentAI Hook
 *
 * Combines Zustand store state with TanStack Query for a unified
 * interface to the Omnipresent AI system. Handles:
 * - Chat mode transitions
 * - AI message sending via N8N workflows
 * - Context-aware queries
 * - Keyboard shortcuts
 */

import { useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useOmnipresentAIStore,
  useAIChatMode,
  useAIInteractionMode,
  useAIMessages,
  useAIContext,
  useAIProcessing,
  useAIActions,
  ChatMode,
  InteractionMode,
  ChatMessage,
  ContextType,
} from '../stores';
import { queryKeys } from '../services/query/queryKeys';
import { useAuth } from '../contexts/AuthContext';
import { useClientProfile } from './useClientProfile';

// N8N webhook endpoint for AI chat (configured via env)
const AI_WEBHOOK_URL = import.meta.env.VITE_N8N_AI_WEBHOOK_URL || '';

interface SendMessageParams {
  content: string;
  context?: {
    type: ContextType;
    data?: Record<string, unknown>;
  };
}

interface AIResponse {
  output?: string; // N8N returns "output" field
  message?: string; // Fallback for other formats
  metadata?: {
    tokens?: number;
    model?: string;
    latency?: number;
  };
  actions?: Array<{
    type: string;
    payload: Record<string, unknown>;
  }>;
}

/**
 * Main hook for interacting with the Omnipresent AI system.
 * Combines local Zustand state with TanStack Query mutations.
 */
export const useOmnipresentAI = () => {
  const queryClient = useQueryClient();

  // Zustand store selectors
  const mode = useAIChatMode();
  const interactionMode = useAIInteractionMode();
  const messages = useAIMessages();
  const currentContext = useAIContext();
  const { isProcessing, isTyping, error } = useAIProcessing();
  const actions = useAIActions();

  // Input state from store
  const inputValue = useOmnipresentAIStore((state) => state.inputValue);
  const isTransitioning = useOmnipresentAIStore((state) => state.isTransitioning);
  const preferences = useOmnipresentAIStore((state) => state.preferences);
  const sessionId = useOmnipresentAIStore((state) => state.sessionId);

  // Auth context
  const { currentUser } = useAuth();
  const { data: clientProfile } = useClientProfile(currentUser?.email);

  // TanStack mutation for sending messages to N8N
  const sendMessageMutation = useMutation({
    mutationFn: async (params: SendMessageParams): Promise<AIResponse> => {
      if (!AI_WEBHOOK_URL) {
        // Development fallback - echo mode
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return {
          message: `[Dev Mode] Received: "${params.content}"${params.context ? ` with context: ${params.context.type}` : ''}`,
          metadata: { model: 'dev-echo', latency: 1000 },
        };
      }

      const response = await fetch(AI_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'sendMessage',
          sessionId: sessionId,
          chatInput: params.content,
          metadata: {
            userId: currentUser?.uid || '',
            userEmail: currentUser?.email || '',
            clientId: clientProfile?.client_id || '',
            clientName: clientProfile?.client_name || ''
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`AI request failed: ${response.statusText}`);
      }

      // Check if response is truly streaming (via content-type header)
      const contentType = response.headers.get('content-type') || '';
      const isSSE = contentType.includes('text/event-stream');

      if (isSSE && response.body) {
        // Handle true Server-Sent Events streaming
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';

        // Create initial empty message for streaming updates
        actions.addMessage({
          role: 'assistant',
          content: '',
          context: currentContext
            ? { type: currentContext.type, data: currentContext.data }
            : undefined,
        });

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });

            // Parse SSE format (data: {...})
            const lines = chunk.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const dataStr = line.slice(6).trim();
                if (dataStr === '[DONE]') continue;

                try {
                  const data = JSON.parse(dataStr);
                  const content = data.output || data.message || data.chunk || '';
                  if (content) {
                    accumulatedContent += content;
                    actions.updateLastMessage(accumulatedContent);
                  }
                } catch (e) {
                  // Treat as plain text chunk
                  if (dataStr) {
                    accumulatedContent += dataStr;
                    actions.updateLastMessage(accumulatedContent);
                  }
                }
              }
            }
          }

          return { output: accumulatedContent };
        } catch (error) {
          console.error('Streaming error:', error);
          throw error;
        }
      } else {
        // Non-streaming: parse as regular JSON
        return response.json();
      }
    },
    onMutate: () => {
      actions.setProcessing(true);
    },
    onSuccess: (data) => {
      // For streaming, the message is already added/updated during the stream
      // Only add message if it wasn't a streaming response
      const lastMessage = messages[messages.length - 1];
      const isStreamingResponse = lastMessage?.role === 'assistant' && lastMessage.content;

      if (!isStreamingResponse) {
        // Non-streaming response: Add AI response to messages
        const responseContent = data.output || data.message || 'No response received';

        actions.addMessage({
          role: 'assistant',
          content: responseContent,
          context: currentContext
            ? { type: currentContext.type, data: currentContext.data }
            : undefined,
          metadata: data.metadata,
        });
      }

      // Handle any AI-triggered actions
      if (data.actions?.length) {
        data.actions.forEach((action) => {
          handleAIAction(action.type, action.payload);
        });
      }
    },
    onError: (error: Error) => {
      actions.setError(error.message);
    },
    onSettled: () => {
      actions.setProcessing(false);
    },
  });

  // Handle AI-triggered actions (e.g., navigate, filter, etc.)
  const handleAIAction = useCallback(
    (actionType: string, payload: Record<string, unknown>) => {
      switch (actionType) {
        case 'invalidate-query':
          if (payload.queryKey && Array.isArray(payload.queryKey)) {
            queryClient.invalidateQueries({ queryKey: payload.queryKey });
          }
          break;
        case 'expand-chat':
          actions.setMode((payload.mode as ChatMode) || 'sidebar');
          break;
        // Add more action handlers as needed
        default:
          console.warn(`Unknown AI action type: ${actionType}`);
      }
    },
    [queryClient, actions]
  );

  // Send message handler
  const sendMessage = useCallback(
    async (content?: string) => {
      const messageContent = content || inputValue.trim();
      if (!messageContent) return;

      // Add user message to store
      actions.addMessage({
        role: 'user',
        content: messageContent,
        context: currentContext
          ? { type: currentContext.type, data: currentContext.data }
          : undefined,
      });

      // Clear input
      actions.setInputValue('');

      // Send to AI
      await sendMessageMutation.mutateAsync({
        content: messageContent,
        context: currentContext
          ? { type: currentContext.type, data: currentContext.data }
          : undefined,
      });
    },
    [inputValue, currentContext, actions, sendMessageMutation]
  );

  // Auto-save conversation when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Debounce saves to avoid excessive writes
      const timeoutId = setTimeout(() => {
        actions.saveCurrentConversation();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [messages, actions]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to toggle chat
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        actions.toggleMode();
      }

      // Escape to minimize when in sidebar/fullscreen
      if (e.key === 'Escape' && mode !== 'hidden') {
        e.preventDefault();
        actions.minimize();
      }

      // Cmd/Ctrl + Shift + K for fullscreen
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'K') {
        e.preventDefault();
        actions.expandToFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, actions]);

  return {
    // State
    mode,
    interactionMode,
    messages,
    currentContext,
    inputValue,
    isProcessing,
    isTyping,
    isTransitioning,
    error,
    preferences,
    currentUser,

    // Actions
    setMode: actions.setMode,
    toggleMode: actions.toggleMode,
    expandToSidebar: actions.expandToSidebar,
    expandToFullscreen: actions.expandToFullscreen,
    minimize: actions.minimize,
    setInteractionMode: actions.setInteractionMode,
    toggleInteractionMode: actions.toggleInteractionMode,
    setInputValue: actions.setInputValue,
    sendMessage,
    deleteMessage: actions.deleteMessage,
    clearMessages: actions.clearMessages,
    captureContext: actions.captureContext,
    clearContext: actions.clearContext,
    reset: actions.reset,

    // Conversation actions
    createNewConversation: actions.createNewConversation,
    loadConversation: actions.loadConversation,
    deleteConversation: actions.deleteConversation,
    renameConversation: actions.renameConversation,
    saveCurrentConversation: actions.saveCurrentConversation,

    // Mutation state
    isSending: sendMessageMutation.isPending,
  };
};

/**
 * Hook for capturing context from the current view.
 * Used by view components to inform the AI of current context.
 */
export const useContextCapture = () => {
  const { captureContext, clearContext } = useAIActions();

  const capture = useCallback(
    (type: ContextType, data: Record<string, unknown>) => {
      captureContext({ type, data });
    },
    [captureContext]
  );

  return {
    capture,
    clear: clearContext,
  };
};

/**
 * Hook for AI chat mode only (lightweight).
 * Use when you only need mode state without full AI functionality.
 */
export const useChatMode = () => {
  const mode = useAIChatMode();
  const { setMode, toggleMode, minimize } = useAIActions();

  return {
    mode,
    setMode,
    toggleMode,
    minimize,
    isOpen: mode !== 'hidden',
    isSidebar: mode === 'sidebar',
    isFullscreen: mode === 'fullscreen',
  };
};
